/**
 * /fr/wiki-entreprise — knowledge-base concept page (FR). Targets wiki
 * entreprise (40) / wiki d'entreprise (20). Links /fr/comparisons/notion-ai +
 * /fr/documentation-produit.
 */
import { MarketingShell } from './MarketingShell';

export function FrWikiEntreprise() {
  return (
    <MarketingShell
      currentLocale="fr"
      hreflangPath="/wiki-entreprise"
      hreflangLocales={['fr']}
      documentTitle="Wiki d'entreprise : comment le construire (et le maintenir) · Draftlize"
      documentDescription="Monter un wiki d'entreprise utile : structure, gouvernance, et pourquoi un wiki figé devient un cimetière — la solution par le contexte vivant."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Base de connaissances</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            Le wiki d'entreprise<br />
            <em>qui ne devient pas un cimetière.</em>
          </h1>
          <p className="lede">
            Un wiki d'entreprise centralise ce que l'équipe sait : procédures, décisions, onboarding, runbooks. Sur le papier, c'est la mémoire de l'organisation. En pratique, la plupart deviennent des <em>cimetières</em> : des pages écrites une fois, jamais relues, périmées en silence, où l'on ne sait plus ce qui est encore vrai. Voici comment le structurer — et comment éviter qu'il pourrisse.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Pourquoi la plupart des wikis meurent.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            Le problème n'est pas l'outil, c'est le modèle : du texte plat, sans lien avec les décisions qui le rendent vrai ou faux. Quand une décision change, aucune page ne le sait. Six mois plus tard, la moitié du wiki est fausse — mais rien ne le signale, alors on cesse d'y croire.
          </p>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: 0 }}>
            Bonnes bases quand même : structure et gouvernance claires, un propriétaire par espace, une source unique de vérité. Reliez ensuite les pages à la <a href="/fr/documentation-produit" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>documentation produit</a> et aux décisions.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Wiki figé vs contexte vivant</span>
            <h2 className="serif">Un savoir qui reste vrai, <em>pas un cimetière de pages.</em></h2>
          </div>
          <p>Un wiki stocke du texte ; un substrat de décisions garde le savoir à jour et signale ce qui périme.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Wiki classique</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Forme</td><td className="ours yes">Décisions liées (cartes)</td><td className="partial">Pages de texte plat</td></tr>
            <tr><td>Une décision change</td><td className="ours yes">Le dépendant passe « stale »</td><td className="no">Rien ne le sait</td></tr>
            <tr><td>Six mois plus tard</td><td className="ours yes">À jour ou signalé</td><td className="no">On ne s'y fie plus</td></tr>
            <tr><td>Prose libre, gros wiki d'équipe</td><td className="no">Pas l'objectif</td><td className="yes">excellent</td></tr>
            <tr><td>Lisible par un agent IA</td><td className="ours yes">via MCP</td><td className="partial">texte plat</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Un wiki d'entreprise ne meurt pas faute de pages. Il meurt parce que rien ne dit lesquelles sont encore vraies.
        </blockquote>
        <cite>Un savoir produit vivant, pas un wiki que personne ne relit. C'est Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Questions fréquentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Wiki d\'entreprise ou base de connaissances ?', a: 'Souvent synonymes. Un wiki met l\'accent sur l\'édition collaborative ; une base de connaissances sur la consultation. Dans les deux cas, le risque est le même : la dérive.' },
            { q: 'Notion ou Confluence ?', a: 'Les deux sont d\'excellents wikis. Le vrai enjeu n\'est pas l\'outil mais de relier le savoir aux décisions pour qu\'il reste vrai.' },
            { q: 'Comment éviter l\'obsolescence ?', a: 'En faisant du savoir une dérivée des décisions plutôt qu\'une copie figée. Quand une décision change, Draftlize signale ce qui devient périmé.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>
                {f.a}
                {i === 1 && (<>{' '}<a href="/fr/comparisons/notion-ai" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>Draftlize vs Notion</a>{'.'}</>)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Un savoir vivant, pas un cimetière <em>— gratuit avec 5 $.</em></h2>
        </div>
        <div className="trust-strip">
          <span>5 $ offerts à l'inscription</span>
          <span>Payez seulement ce que vous utilisez</span>
          <span>Le solde n'expire jamais</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Gardez votre wiki pour la prose. Mettez les décisions produit dans Draftlize — reliées, à jour, et signalées quand elles périment.
          </p>
          <a className="btn-primary" href="/">Commencer gratuitement avec 5 $</a>
        </div>
      </section>
    </MarketingShell>
  );
}
